import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { useModalStore } from '@/hooks/useModalStore'
import { morphToast } from '@/components/ui/Toast'
import { ALL_DIALECTS } from '@/database/dialectRegistry'
import { projectApi } from '@/api/projectApi'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { navigateOrShowTabLimit } from '@/stores/tabLimitStore'
import { ApiError } from '@/api/httpClient'
import { openPlanDialogForError } from '@/utils/openPlanDialogForError'
import type { DatabaseDialect } from '@/types'

const DIALECT_OPTIONS = ALL_DIALECTS.map((dialect) => ({
  value: dialect.value,
  label: dialect.label,
}))

interface NewProjectModalProps {
  workspaceId?: number
}

export function NewProjectModal({ workspaceId }: NewProjectModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const open = useModalStore((state) => state.newProjectOpen)
  const close = useModalStore((state) => state.closeNewProject)
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const insertProject = useProjectsStore((s) => s.insertProject)

  const [name, setName] = useState('')
  const [dialect, setDialect] = useState<DatabaseDialect>('postgresql')
  const [nameError, setNameError] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('MorphERD')
    setDialect('postgresql')
    setNameError(false)
  }, [open])

  const normalizedName = useMemo(() => name.trim(), [name])
  const targetWorkspaceId = workspaceId ?? currentWorkspace?.id ?? null

  async function handleCreate() {
    if (!normalizedName) { setNameError(true); return }
    if (!targetWorkspaceId) {
      morphToast.error(t('import.noWorkspace', 'Nenhum workspace ativo.'))
      return
    }
    setSubmitting(true)
    let projectId: number | null = null
    try {
      // 1. Criar projeto
      const project = await projectApi.create(targetWorkspaceId, { name: normalizedName, dialect }, { silent: true })
      projectId = project.id

      // 2. Atualizar lista local e navegar para o editor.
      // O backend já cria o draft compartilhado inicial; o primeiro commit
      // só precisa acontecer quando a pessoa realmente decidir versionar.
      insertProject(project)
      close()
      navigateOrShowTabLimit(navigate, `/workspace/${currentWorkspace?.slug ?? ''}/projects/${project.id}/editor`)
      morphToast.success(t('prompts.newProjectDone', 'Projeto criado!'))
    } catch (err) {
      // Rollback: remover projeto temporário se a criação falhar depois do POST inicial.
      if (projectId) {
        try {
          await projectApi.delete(projectId, { silent: true })
        } catch {
          try {
            await projectApi.patch(projectId, { is_active: false }, { silent: true })
          } catch { /* melhor esforço */ }
        }
      }
      openPlanDialogForError(err)
      if (err instanceof ApiError && err.code === 'PROJECT_NAME_DUPLICATE') {
        morphToast.error(t('newProject.duplicate', 'Já existe um projeto com este nome neste workspace.'))
      } else {
        morphToast.error(err instanceof Error ? err.message : t('common.error', 'Erro ao criar projeto'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={t('newProject.title')}
      onClose={close}
      footer={(
        <>
          <Button variant="ghost" onClick={close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={submitting}>
            {submitting ? t('common.loading', 'Aguarde...') : t('newProject.create')}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {t('newProject.nameLabel')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(false) }}
            placeholder={t('newProject.namePlaceholder')}
            className="morph-input"
            disabled={submitting}
          />
          {nameError ? (
            <div className="mt-1 text-[11px]" style={{ color: 'var(--color-danger)' }}>
              {t('newProject.nameRequired')}
            </div>
          ) : null}
        </div>

        <div>
          <label className="mb-1 block text-[11px]" style={{ color: 'var(--text-dim)' }}>
            {t('newProject.dialectLabel')}
          </label>
          <MorphSelect<DatabaseDialect>
            ariaLabel={t('newProject.dialectLabel')}
            value={dialect}
            options={DIALECT_OPTIONS}
            onChange={(d) => { if (d) setDialect(d) }}
            disabled={submitting}
          />
        </div>
      </div>
    </Dialog>
  )
}
